import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

import { API_BASE as API } from '../config';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
      toast.success('Reset link generated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg" data-testid="forgot-password-success-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-3 p-3 bg-green-50 rounded-full w-fit">
              <CheckCircle className="w-7 h-7 text-green-500" />
            </div>
            <CardTitle className="text-2xl font-bold">Check Your Email</CardTitle>
            <CardDescription>If an account exists with {email}, a password reset link has been sent.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {resetToken && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md" data-testid="reset-token-display">
                <p className="text-xs text-blue-600 font-medium mb-1">Reset Link (Demo Mode)</p>
                <Link
                  to={`/reset-password?token=${resetToken}`}
                  className="text-sm text-blue-700 hover:underline break-all"
                  data-testid="reset-password-link"
                >
                  Click here to reset your password
                </Link>
              </div>
            )}
            <Button variant="outline" className="w-full" onClick={() => { setSent(false); setEmail(''); }} data-testid="try-another-email-btn">
              Try another email
            </Button>
            <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-200 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="forgot-password-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-3 p-3 bg-amber-50 rounded-full w-fit">
            <Mail className="w-7 h-7 text-amber-500" />
          </div>
          <CardTitle className="text-2xl font-bold">Forgot Password?</CardTitle>
          <CardDescription>Enter your email and we'll send you a reset link</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="forgot-email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>
            <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600" disabled={submitting} data-testid="forgot-submit-btn">
              {submitting ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
          <Link to="/login" className="flex items-center justify-center gap-1 mt-4 text-sm text-gray-600 hover:text-gray-900" data-testid="back-to-login-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Sign In
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
